import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { procurementApi as api } from 'api/procurement';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

function DocumentUploadDialog({ isOpen, onClose, onUploadSuccess, poID }) {
    const [uploading, setUploading] = useState(false);
    const [notes, setNotes] = useState('');
    const [file, setFile] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setNotes('');
            setFile(null);
        }
    }, [isOpen]);

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();

            if (!file) {
                toast.error('Please select a file to upload');
                return;
            }

            setUploading(true);

            try {
                const formData = {
                    document: file,
                    notes: notes || 'Other',
                };

                const response = await api.uploadPODocument(poID, formData);

                if (response.status === false) {
                    toast.error(response.msg || 'Failed to upload document');
                    return;
                }

                toast.success('Document uploaded successfully');
                onUploadSuccess(poID);
                onClose();
            } catch (error) {
                toast.error('An error occurred while uploading the document');
                console.error('Upload error:', error);
            } finally {
                setUploading(false);
            }
        },
        [file, notes, onClose, onUploadSuccess, poID],
    );

    const handleFileChange = useCallback((e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Validate file type
            if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
                toast.error('Only PNG, JPEG, and PDF files are allowed');
                setFile(null);
                e.target.value = '';
                return;
            }
            setFile(selectedFile);
        }
    }, []);

    const handleOpenChange = useCallback(
        (open) => {
            if (!open && uploading) {
                return;
            }
            if (!open) {
                onClose();
            }
        },
        [onClose, uploading],
    );

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent
                className="sm:max-w-[500px]"
                onEscapeKeyDown={(event) => {
                    if (uploading) {
                        event.preventDefault();
                    }
                }}
                onInteractOutside={(event) => {
                    if (uploading) {
                        event.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                    <DialogDescription>Upload a document for PO #{poID}</DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="notes">Document Type</Label>
                            <Select value={notes} onValueChange={setNotes}>
                                <SelectTrigger id="notes">
                                    <SelectValue placeholder="Select document type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="BOL">BOL</SelectItem>
                                    <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="document">Document</Label>
                            <Input
                                id="document"
                                type="file"
                                accept=".png,.jpeg,.jpg,.pdf"
                                onChange={handleFileChange}
                                disabled={uploading}
                            />
                            {file && <p className="text-sm text-gray-600 mt-1">Selected: {file.name}</p>}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={uploading}>
                            {uploading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export { DocumentUploadDialog };
